import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { API_BASE } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { FileDown, Pencil, Send } from "lucide-react";

import { toast } from "sonner";

async function downloadPdf(id, code, template = "") {
  const q = template ? `?template=${template}` : "";
  try {
    const res = await fetch(`${API_BASE}/exports/invoice/${id}/pdf${q}`, { credentials: "include" });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      toast.error(`Download failed (${res.status})`, { description: txt.slice(0, 120) });
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${code.replace(/\//g, "_")}-${template || "invoice"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error("Download failed", { description: e?.message || String(e) });
  }
}
async function downloadExcel() {
  try {
    const res = await fetch(`${API_BASE}/exports/report/invoices.xlsx`, { credentials: "include" });
    if (!res.ok) { toast.error(`Excel export failed (${res.status})`); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "invoices.xlsx";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error("Excel export failed", { description: e?.message || String(e) });
  }
}

export default function Invoices() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = ["admin", "accounts"].includes(user?.role);
  const [editInv, setEditInv] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const { data } = useQuery({ queryKey: ["invoices"], queryFn: () => api.get("/accounts/invoices?limit=500").then((r) => r.data) });
  const allItems = data?.items || [];
  const total = allItems.reduce((s, i) => s + (i.total || 0), 0);
  const outstanding = allItems.filter((i) => i.payment_status !== "paid").reduce((s, i) => s + (i.total - (i.paid_amount || 0)), 0);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(allItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = allItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/accounts/invoices/${id}`, patch),
    onSuccess: () => {
      toast.success("Invoice updated");
      setEditInv(null);
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });

  const sendWa = useMutation({
    mutationFn: ({ id, phone }) => api.post(`/accounts/invoices/${id}/send-whatsapp`, phone ? { phone } : {}),
    onSuccess: (res) => {
      const d = res.data || {};
      if (d.delivered) {
        toast.success("WhatsApp sent", { description: `Delivered to ${d.phone}` });
      } else {
        toast.warning("WhatsApp queued (gateway not delivered)", {
          description: d.reason || "Enable a WhatsApp provider in Settings → Integrations to deliver messages. The share link has been generated.",
        });
      }
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "WhatsApp send failed"),
  });

  const handleSendWa = (inv) => {
    const phone = window.prompt(
      `Send invoice ${inv.code} via WhatsApp?\n\nCustomer phone: ${inv.customer_phone || "—"}\nLeave blank to use customer's saved phone, or enter a phone number:`,
      "",
    );
    if (phone === null) return; // cancelled
    sendWa.mutate({ id: inv.id, phone: phone.trim() });
  };

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Invoices</h1>
          <p className="text-sm text-muted-foreground">{data?.total || 0} invoices · ₹{outstanding.toLocaleString("en-IN")} outstanding</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadExcel} data-testid="btn-excel-invoices"><FileDown size={12} className="mr-1" /> Excel export</Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPI label="Total invoices" value={data?.total || 0} />
        <KPI label="Total billed" value={`₹${total.toLocaleString("en-IN")}`} />
        <KPI label="Outstanding" value={`₹${outstanding.toLocaleString("en-IN")}`} highlight />
        <KPI label="Collected" value={`₹${(total - outstanding).toLocaleString("en-IN")}`} />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full min-w-[1100px]">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3">Invoice</th>
                  <th className="whitespace-nowrap px-4 py-3">Date</th>
                  <th className="whitespace-nowrap px-4 py-3">Order</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="whitespace-nowrap px-4 py-3 text-right">Qty (L)</th>
                  <th className="whitespace-nowrap px-4 py-3 text-right">Subtotal</th>
                  <th className="whitespace-nowrap px-4 py-3 text-right">GST</th>
                  <th className="whitespace-nowrap px-4 py-3 text-right">Total</th>
                  <th className="whitespace-nowrap px-4 py-3">Payment</th>
                  <th className="whitespace-nowrap px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((i) => (
                  <tr key={i.id} className="border-b border-border/40 text-sm">
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs font-semibold">{i.code}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs">{new Date(i.invoice_date).toLocaleDateString()}</td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs"><Link to={`/app/orders/${i.order_id}`} className="hover:text-primary">{i.order_code}</Link></td>
                    <td className="px-4 py-3">
                      <div className="max-w-[220px] truncate" title={i.customer_name}>{i.customer_name}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-mono">{i.quantity_litres}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-mono">₹{i.subtotal.toLocaleString("en-IN")}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-mono">₹{(i.cgst + i.sgst + i.igst).toLocaleString("en-IN")}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-mono font-semibold">₹{i.total.toLocaleString("en-IN")}</td>
                    <td className="whitespace-nowrap px-4 py-3"><StatusBadge status={i.payment_status} /></td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {canEdit && (
                          <button
                            onClick={() => setEditInv(i)}
                            className="rounded border border-border px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:border-primary"
                            title="Edit invoice"
                            data-testid={`inv-edit-${i.code}`}
                          >
                            <Pencil size={10} className="inline -translate-y-px" /> Edit
                          </button>
                        )}
                        <button onClick={() => downloadPdf(i.id, i.code, "standard")} className="rounded border border-border px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:border-primary" title="Standard PDF" data-testid={`pdf-std-${i.code}`}>Standard</button>
                        <button onClick={() => downloadPdf(i.id, i.code, "spreadsheet")} className="rounded border border-border px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:border-primary" title="Spreadsheet PDF" data-testid={`pdf-ss-${i.code}`}>Spreadsheet</button>
                        {canEdit && (
                          <button
                            onClick={() => handleSendWa(i)}
                            disabled={sendWa.isPending}
                            className="rounded border border-emerald-400 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 hover:border-emerald-500 hover:bg-emerald-100 disabled:opacity-50 dark:bg-emerald-950/30 dark:text-emerald-300"
                            title="Send invoice to customer via WhatsApp"
                            data-testid={`inv-send-wa-${i.code}`}
                          >
                            <Send size={10} className="inline -translate-y-px" /> WhatsApp
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {allItems.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-muted-foreground">No invoices yet. Deliver an order to generate.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border/60 px-4 py-3 text-xs" data-testid="invoices-pagination">
              <div className="text-muted-foreground">
                Showing <b>{(currentPage - 1) * PAGE_SIZE + 1}</b>–<b>{Math.min(currentPage * PAGE_SIZE, allItems.length)}</b> of <b>{allItems.length}</b> invoices
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={currentPage === 1}
                  className="rounded border border-border px-2 py-1 font-semibold hover:border-primary disabled:opacity-40"
                  data-testid="page-first"
                >« First</button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded border border-border px-2 py-1 font-semibold hover:border-primary disabled:opacity-40"
                  data-testid="page-prev"
                >‹ Prev</button>
                {/* Page number buttons — show up to 7 around current */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 2)
                  .reduce((acc, n, idx, arr) => {
                    if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…");
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, idx) =>
                    n === "…" ? (
                      <span key={`gap-${idx}`} className="px-1 text-muted-foreground">…</span>
                    ) : (
                      <button
                        key={n}
                        onClick={() => setPage(n)}
                        className={`min-w-[32px] rounded border px-2 py-1 font-semibold ${
                          n === currentPage
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border hover:border-primary"
                        }`}
                        data-testid={`page-${n}`}
                      >{n}</button>
                    )
                  )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded border border-border px-2 py-1 font-semibold hover:border-primary disabled:opacity-40"
                  data-testid="page-next"
                >Next ›</button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="rounded border border-border px-2 py-1 font-semibold hover:border-primary disabled:opacity-40"
                  data-testid="page-last"
                >Last »</button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <InvoiceEditor
        open={!!editInv}
        inv={editInv}
        onClose={() => setEditInv(null)}
        onSave={(patch) => update.mutate({ id: editInv.id, patch })}
        saving={update.isPending}
      />
    </div>
  );
}

function InvoiceEditor({ open, inv, onClose, onSave, saving }) {
  const [form, setForm] = useState({});
  React.useEffect(() => {
    if (inv) {
      setForm({
        invoice_date: (inv.invoice_date || "").slice(0, 10),
        hsn_code: inv.hsn_code || "27101930",
        quantity_litres: inv.quantity_litres || 0,
        rate: inv.rate || 0,
        gst_rate: inv.gst_rate || 18,
        cess: inv.cess || 0,
        customer_name: inv.customer_name || "",
        customer_gstin: inv.customer_gstin || "",
        customer_state: inv.customer_state || "",
        place_of_supply: inv.place_of_supply || "",
        payment_status: inv.payment_status || "unpaid",
        paid_amount: inv.paid_amount || 0,
        notes: inv.notes || "",
      });
    }
  }, [inv]);
  if (!inv) return null;

  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const qty = Number(form.quantity_litres || 0);
  const rate = Number(form.rate || 0);
  const gstRate = Number(form.gst_rate || 0);
  const cess = Number(form.cess || 0);
  const subtotal = +(qty * rate).toFixed(2);
  const cust = (form.customer_state || "").trim().toLowerCase();
  const pos = (form.place_of_supply || "").trim().toLowerCase();
  const interstate = !!cust && !!pos && cust !== pos;
  const cgst = interstate ? 0 : +(subtotal * gstRate / 200).toFixed(2);
  const sgst = interstate ? 0 : +(subtotal * gstRate / 200).toFixed(2);
  const igst = interstate ? +(subtotal * gstRate / 100).toFixed(2) : 0;
  const total = +(subtotal + cgst + sgst + igst + cess).toFixed(2);

  const isPaid = inv.payment_status === "paid";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="inv-editor-form">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Edit invoice <span className="font-mono text-primary">{inv.code}</span>
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Editing recomputes subtotal, GST, and total automatically.
            {isPaid && " · This invoice is fully paid — only Notes / Payment Status can be edited."}
          </p>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Invoice date">
              <Input type="date" value={form.invoice_date} onChange={set("invoice_date")} disabled={isPaid} data-testid="inv-date" />
            </Field>
            <Field label="HSN code">
              <Input value={form.hsn_code} onChange={set("hsn_code")} disabled={isPaid} className="font-mono" data-testid="inv-hsn" />
            </Field>
            <Field label="Customer name">
              <Input value={form.customer_name} onChange={set("customer_name")} disabled={isPaid} data-testid="inv-cust-name" />
            </Field>
            <Field label="Customer GSTIN">
              <Input value={form.customer_gstin} onChange={set("customer_gstin")} disabled={isPaid} className="font-mono uppercase" data-testid="inv-cust-gstin" />
            </Field>
            <Field label="Customer state">
              <Input value={form.customer_state} onChange={set("customer_state")} disabled={isPaid} data-testid="inv-cust-state" />
            </Field>
            <Field label="Place of supply">
              <Input value={form.place_of_supply} onChange={set("place_of_supply")} disabled={isPaid} data-testid="inv-pos" />
            </Field>
          </div>

          <div className="rounded-lg border border-border/60 p-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Line items</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <Field label="Quantity (L)">
                <Input type="number" step="0.01" value={form.quantity_litres} onChange={set("quantity_litres")} disabled={isPaid} data-testid="inv-qty" />
              </Field>
              <Field label="Rate (₹ / L)">
                <Input type="number" step="0.01" value={form.rate} onChange={set("rate")} disabled={isPaid} data-testid="inv-rate" />
              </Field>
              <Field label="GST rate (%)">
                <Input type="number" step="0.01" value={form.gst_rate} onChange={set("gst_rate")} disabled={isPaid} data-testid="inv-gst-rate" />
              </Field>
              <Field label="Cess (₹)">
                <Input type="number" step="0.01" value={form.cess} onChange={set("cess")} disabled={isPaid} data-testid="inv-cess" />
              </Field>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border/60 pt-4 text-sm md:grid-cols-5">
              <SummaryCell l="Subtotal" v={`₹${subtotal.toLocaleString("en-IN")}`} />
              <SummaryCell l={interstate ? "CGST" : "CGST"} v={`₹${cgst.toLocaleString("en-IN")}`} muted={interstate} />
              <SummaryCell l="SGST" v={`₹${sgst.toLocaleString("en-IN")}`} muted={interstate} />
              <SummaryCell l="IGST" v={`₹${igst.toLocaleString("en-IN")}`} muted={!interstate} />
              <SummaryCell l="Total" v={`₹${total.toLocaleString("en-IN")}`} highlight />
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              {interstate ? "Interstate supply detected — IGST applied." : "Intrastate supply — CGST + SGST applied."}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Payment status">
              <Select value={form.payment_status} onValueChange={(v) => setForm((f) => ({ ...f, payment_status: v }))}>
                <SelectTrigger data-testid="inv-payment-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Paid amount (₹)">
              <Input type="number" step="0.01" value={form.paid_amount} onChange={set("paid_amount")} data-testid="inv-paid-amt" />
            </Field>
          </div>

          <Field label="Notes / remarks">
            <Textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Optional internal note or revision reason" data-testid="inv-notes" />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={saving} data-testid="btn-save-invoice">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const SummaryCell = ({ l, v, highlight, muted }) => (
  <div className={muted ? "opacity-40" : ""}>
    <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{l}</div>
    <div className={`mt-0.5 font-mono ${highlight ? "text-lg font-bold text-primary" : "font-semibold"}`}>{v}</div>
  </div>
);

const KPI = ({ label, value, highlight }) => (
  <div className="kpi-card">
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`mt-2 font-display text-2xl font-black ${highlight ? "text-primary" : ""}`}>{value}</div>
  </div>
);
